import { useMemo } from "react";

import {
  AccessLevelEnum,
  AccessStatusEnum,
  ConnectionTypeEnum,
  DestinationTypeEnum,
  NetworkCategory,
  NetworkStatusEnum,
  NetworkTypeEnum,
  SimStatusEnum,
  SubjectTypeEnum,
} from "@/constants/network";
import { defaultNS, useTranslation } from "@/lib/i18n";

export const useNetworkFormHelpers = () => {
  const networkTypeOptions = useNetworkTypeOptions();
  const connectionTypeOptions = useConnectionTypeOptions();
  const networkCategoryOptions = useNetworkCategoryOptions();
  const accessLevelOptions = useAccessLevelOptions();
  const accessStatusOptions = useAccessStatusOptions();
  const subjectTypeOptions = useSubjectTypeOptions();
  const networkStatusOptions = useNetworkStatusOptions();
  const simStatusOptions = useSimStatusOptions();

  return {
    networkTypeOptions,
    connectionTypeOptions,
    networkCategoryOptions,
    accessLevelOptions,
    accessStatusOptions,
    subjectTypeOptions,
    networkStatusOptions,
    simStatusOptions,
  };
};

/**
 * ネットワークカテゴリのオプションを取得
 */
export const useNetworkCategoryOptions = () => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.filters",
  });
  return useMemo(
    () => [
      {
        value: NetworkCategory.SHARED_INTERNAL,
        label: t("networkCategoryValues.SHARED_INTERNAL"),
      },
      {
        value: NetworkCategory.PERSONAL_USE,
        label: t("networkCategoryValues.PERSONAL_USE"),
      },
      {
        value: NetworkCategory.COMPANY_ASSET,
        label: t("networkCategoryValues.COMPANY_ASSET"),
      },
      {
        value: NetworkCategory.CUSTOMER_ENVIRONMENT,
        label: t("networkCategoryValues.CUSTOMER_ENVIRONMENT"),
      },
    ],
    [t],
  );
};

/**
 * ネットワークタイプのオプションを取得
 */
export const useNetworkTypeOptions = () => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.filters",
  });
  return useMemo(
    () => [
      {
        value: NetworkTypeEnum.INTERNAL,
        label: t("networkTypeValues.INTERNAL"),
      },
      {
        value: NetworkTypeEnum.EXTERNAL,
        label: t("networkTypeValues.EXTERNAL"),
      },
      { value: NetworkTypeEnum.VPN, label: t("networkTypeValues.VPN") },
      {
        value: NetworkTypeEnum.DEDICATED_LINE,
        label: t("networkTypeValues.DEDICATED_LINE"),
      },
      { value: NetworkTypeEnum.CLOUD, label: t("networkTypeValues.CLOUD") },
    ],
    [t],
  );
};

/**
 * 接続タイプのオプションを取得
 */
export const useConnectionTypeOptions = () => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.filters",
  });
  return useMemo(
    () => [
      {
        value: ConnectionTypeEnum.WIRED,
        label: t("connectionTypeValues.WIRED"),
      },
      {
        value: ConnectionTypeEnum.WIRELESS,
        label: t("connectionTypeValues.WIRELESS"),
      },
      { value: ConnectionTypeEnum.BOTH, label: t("connectionTypeValues.BOTH") },
    ],
    [t],
  );
};

/**
 * ネットワークステータスのオプションを取得
 */
export const useNetworkStatusOptions = () => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  return useMemo(
    () => [
      {
        value: NetworkStatusEnum.CONNECTED,
        label: t("networkStatus.CONNECTED"),
      },
      {
        value: NetworkStatusEnum.OUT_OF_SERVICE,
        label: t("networkStatus.OUT_OF_SERVICE"),
      },
      { value: NetworkStatusEnum.ROAMING, label: t("networkStatus.ROAMING") },
      {
        value: NetworkStatusEnum.DISCONNECTED,
        label: t("networkStatus.DISCONNECTED"),
      },
    ],
    [t],
  );
};

/**
 * SIMステータスのオプションを取得
 */
export const useSimStatusOptions = () => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  return useMemo(
    () => [
      { value: SimStatusEnum.ACTIVE, label: t("simStatus.ACTIVE") },
      { value: SimStatusEnum.INACTIVE, label: t("simStatus.INACTIVE") },
      { value: SimStatusEnum.SUSPENDED, label: t("simStatus.SUSPENDED") },
      { value: SimStatusEnum.UNKNOWN, label: t("simStatus.UNKNOWN") },
    ],
    [t],
  );
};

/**
 * アクセスステータスのオプションを取得
 */
export const useAccessStatusOptions = () => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  return useMemo(
    () => [
      { value: AccessStatusEnum.ACTIVE, label: t("accessStatus.ACTIVE") },
      { value: AccessStatusEnum.INACTIVE, label: t("accessStatus.INACTIVE") },
      { value: AccessStatusEnum.SUSPENDED, label: t("accessStatus.SUSPENDED") },
      { value: AccessStatusEnum.EXPIRED, label: t("accessStatus.EXPIRED") },
    ],
    [t],
  );
};

/**
 * アクセスレベルのオプションを取得
 */
export const useAccessLevelOptions = () => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  return useMemo(
    () => [
      {
        value: AccessLevelEnum.FULL_ACCESS,
        label: t("accessLevel.FULL_ACCESS"),
      },
      { value: AccessLevelEnum.READ_ONLY, label: t("accessLevel.READ_ONLY") },
      { value: AccessLevelEnum.LIMITED, label: t("accessLevel.LIMITED") },
      { value: AccessLevelEnum.NO_ACCESS, label: t("accessLevel.NO_ACCESS") },
    ],
    [t],
  );
};

/**
 * サブジェクトタイプのオプションを取得
 */
export const useSubjectTypeOptions = () => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  return useMemo(
    () => [
      { value: SubjectTypeEnum.USER, label: t("subjectType.USER") },
      { value: SubjectTypeEnum.DEVICE, label: t("subjectType.DEVICE") },
    ],
    [t],
  );
};

/**
 * enum値を表示用の翻訳テキストに変換するフック群
 */

export const useNetworkCategoryDisplayValue = (value: string | undefined) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.filters",
  });
  if (!value) return undefined;

  switch (value) {
    case NetworkCategory.SHARED_INTERNAL:
      return t("networkCategoryValues.SHARED_INTERNAL");
    case NetworkCategory.PERSONAL_USE:
      return t("networkCategoryValues.PERSONAL_USE");
    case NetworkCategory.COMPANY_ASSET:
      return t("networkCategoryValues.COMPANY_ASSET");
    case NetworkCategory.CUSTOMER_ENVIRONMENT:
      return t("networkCategoryValues.CUSTOMER_ENVIRONMENT");
    default:
      return undefined;
  }
};

export const useNetworkTypeDisplayValue = (value: string | undefined) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.filters",
  });
  if (!value) return undefined;

  switch (value) {
    case NetworkTypeEnum.INTERNAL:
      return t("networkTypeValues.INTERNAL");
    case NetworkTypeEnum.EXTERNAL:
      return t("networkTypeValues.EXTERNAL");
    case NetworkTypeEnum.VPN:
      return t("networkTypeValues.VPN");
    case NetworkTypeEnum.DEDICATED_LINE:
      return t("networkTypeValues.DEDICATED_LINE");
    case NetworkTypeEnum.CLOUD:
      return t("networkTypeValues.CLOUD");
    default:
      return undefined;
  }
};

export const useConnectionTypeDisplayValue = (value: string | undefined) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.filters",
  });
  if (!value) return undefined;

  switch (value) {
    case ConnectionTypeEnum.WIRED:
      return t("connectionTypeValues.WIRED");
    case ConnectionTypeEnum.WIRELESS:
      return t("connectionTypeValues.WIRELESS");
    case ConnectionTypeEnum.BOTH:
      return t("connectionTypeValues.BOTH");
    default:
      return undefined;
  }
};

export const useDestinationTypeDisplayValue = (value: string | undefined) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.filters",
  });
  if (!value) return undefined;

  switch (value) {
    case DestinationTypeEnum.DEVICE:
      return t("destinationTypes.DEVICE");
    case DestinationTypeEnum.PLACE:
      return t("destinationTypes.PLACE");
    default:
      return undefined;
  }
};

export const useNetworkStatusDisplayValue = (value: string | undefined) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  if (!value) return undefined;

  switch (value) {
    case NetworkStatusEnum.CONNECTED:
      return t("networkStatus.CONNECTED");
    case NetworkStatusEnum.OUT_OF_SERVICE:
      return t("networkStatus.OUT_OF_SERVICE");
    case NetworkStatusEnum.ROAMING:
      return t("networkStatus.ROAMING");
    case NetworkStatusEnum.DISCONNECTED:
      return t("networkStatus.DISCONNECTED");
    default:
      return undefined;
  }
};

export const useSimStatusDisplayValue = (value: string | undefined) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  if (!value) return undefined;

  switch (value) {
    case SimStatusEnum.ACTIVE:
      return t("simStatus.ACTIVE");
    case SimStatusEnum.INACTIVE:
      return t("simStatus.INACTIVE");
    case SimStatusEnum.SUSPENDED:
      return t("simStatus.SUSPENDED");
    case SimStatusEnum.UNKNOWN:
      return t("simStatus.UNKNOWN");
    default:
      return undefined;
  }
};

export const useAccessStatusDisplayValue = (value: string | undefined) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  if (!value) return undefined;

  switch (value) {
    case AccessStatusEnum.ACTIVE:
      return t("accessStatus.ACTIVE");
    case AccessStatusEnum.INACTIVE:
      return t("accessStatus.INACTIVE");
    case AccessStatusEnum.SUSPENDED:
      return t("accessStatus.SUSPENDED");
    case AccessStatusEnum.EXPIRED:
      return t("accessStatus.EXPIRED");
    default:
      return undefined;
  }
};

export const useAccessLevelDisplayValue = (value: string | undefined) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  if (!value) return undefined;

  switch (value) {
    case AccessLevelEnum.FULL_ACCESS:
      return t("accessLevel.FULL_ACCESS");
    case AccessLevelEnum.READ_ONLY:
      return t("accessLevel.READ_ONLY");
    case AccessLevelEnum.LIMITED:
      return t("accessLevel.LIMITED");
    case AccessLevelEnum.NO_ACCESS:
      return t("accessLevel.NO_ACCESS");
    default:
      return undefined;
  }
};

export const useSubjectTypeDisplayValue = (value: string | undefined) => {
  const { t } = useTranslation(defaultNS, {
    keyPrefix: "pages.networks.detail.values",
  });
  if (!value) return undefined;

  switch (value) {
    case SubjectTypeEnum.USER:
      return t("subjectType.USER");
    case SubjectTypeEnum.DEVICE:
      return t("subjectType.DEVICE");
    default:
      return undefined;
  }
};
